import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import {
  Phone, CalendarCheck, FileText, Snowflake, Flame, Wrench, Shovel, Wind,
  BadgeCheck, Clock, MapPin, Store, ChevronRight, Star,
} from 'lucide-react';
import SEO from '../components/SEO';
import Marquee from '../components/Marquee';
import Reviews from '../components/Reviews';
import CTABand from '../components/CTABand';
import FAQSection from '../components/FAQSection';
import LeadForm from '../components/LeadForm';
import { Reveal, LineReveal, Chapter } from '../components/motion';
import { SITE, IMAGES, CITIES, cityPath, localBusinessSchema, faqSchema } from '../data/site';
import { HOME_GALLERY } from '../data/extraImages';

const TRUST_BADGES = [
  { icon: Clock, label: 'Over 60 Years of Service' },
  { icon: BadgeCheck, label: 'Residential & Commercial' },
  { icon: Snowflake, label: 'Heating, Cooling & Plumbing' },
  { icon: MapPin, label: 'Serving Stanfield & Greater Charlotte' },
];

const SERVICE_CARDS = [
  {
    icon: Snowflake,
    title: 'Heating & Air Conditioning',
    text: 'AC repair, HVAC replacement, furnace and heat pump service, and maintenance — backed by American Standard equipment and factory-trained installers.',
    path: '/heating-air-conditioning',
    image: IMAGES.teamVan,
    alt: 'B.E. Holbrooks Co. technicians with their service van for heating and air conditioning in Stanfield, NC',
    testid: 'service-card-hvac',
  },
  {
    icon: Wrench,
    title: 'Plumbing Services',
    text: 'Residential and commercial plumbing, repairs, new construction, custom homes, fixtures, and water and sewer line work — our trade for 60+ years.',
    path: '/plumbing',
    image: IMAGES.plumbing,
    alt: 'Professional plumber repairing residential plumbing in Stanfield, NC',
    testid: 'service-card-plumbing',
  },
  {
    icon: Shovel,
    title: 'Excavation & Trenching',
    text: 'Trenching and excavation for water lines, sewer lines, and site work — done in-house so your underground project has one accountable team.',
    path: '/excavation-trenching',
    image: IMAGES.excavation,
    alt: 'Excavation equipment trenching for a water line project near Stanfield, NC',
    testid: 'service-card-excavation',
  },
  {
    icon: Wind,
    title: 'Indoor Air Quality',
    text: 'Whole-home air purification, filtration, and humidity control that help your family breathe cleaner, healthier air every day.',
    path: '/indoor-air-quality',
    image: IMAGES.acRepair,
    alt: 'Indoor air quality and home comfort solutions in Stanfield, NC',
    testid: 'service-card-iaq',
  },
];

const WHY_POINTS = [
  { title: 'Over 60 Years in Business', text: 'Three generations of local families have trusted us with their comfort. We have been here since 1962 — and we are not going anywhere.' },
  { title: 'Trained Technicians', text: 'Our installers and service technicians are trained professionals, hand-picked and factory-trained as an American Standard Customer Care Dealer.' },
  { title: 'Workmanship First', text: 'We size systems correctly, install them carefully, and stand behind every job. Reputation is everything in a small town — and ours was earned.' },
  { title: 'One Company, Whole Project', text: 'HVAC, plumbing, and excavation under one roof means no juggling contractors when a project crosses trades.' },
];

const HOME_FAQS = [
  { q: 'How long has B.E. Holbrooks Co. been in business?', a: 'We have provided plumbing, heating, and air conditioning services for over 60 years, serving Stanfield and the surrounding North Carolina communities since 1962.' },
  { q: 'What areas do you serve?', a: 'We serve Stanfield, Locust, Charlotte, Concord, Kannapolis, Albemarle, Norwood, Monroe, Gastonia, Huntersville, and surrounding North Carolina communities.' },
  { q: 'Do you offer free estimates?', a: 'Yes. Estimates on HVAC replacement, installation, and larger plumbing projects are free and come with no pressure. Call 704-888-2411 to schedule yours.' },
  { q: 'What HVAC brands do you install?', a: 'We are an American Standard Customer Care Dealer — hand-picked for our installation quality — and we service and repair all major heating and air conditioning brands.' },
  { q: 'Do you handle both residential and commercial work?', a: 'Yes. We serve homeowners, businesses, industrial facilities, and institutions with HVAC, plumbing, excavation, and trenching services.' },
  { q: 'Do you still have the hardware store?', a: 'Yes. Our hardware and garden store at 204 West Stanly Street in Stanfield carries a range of hardware and gardening supplies. Come see us.' },
];

const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE.name,
  url: SITE.domain,
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ['start start', 'end start'] });
  const imgY = useTransform(scrollYProgress, [0, 1], ['0%', '22%']);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  return (
    <>
      <SEO
        title="HVAC & Plumbing Services in Stanfield, NC | B.E. Holbrooks Co."
        description="B.E. Holbrooks Co. provides trusted HVAC, heating, air conditioning, plumbing, excavation, and trenching services in Stanfield, Locust, Charlotte, Concord, Monroe, and nearby NC communities. Call 704-888-2411 today."
        path="/"
        schemas={[localBusinessSchema, websiteSchema, faqSchema(HOME_FAQS)]}
      />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[92svh] overflow-hidden bg-navy grain" data-testid="home-hero">
        <motion.div className="absolute inset-0" style={{ y: imgY }} aria-hidden="true">
          <img
            src={IMAGES.heroTech}
            alt="B.E. Holbrooks Co. service team standing in front of their American Standard HVAC and plumbing service van in Stanfield, NC"
            className="h-[120%] w-full object-cover"
          />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-dark/95 via-navy/75 to-navy/25" aria-hidden="true" />
        <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-navy-dark/90 to-transparent" aria-hidden="true" />
        <div className="absolute -right-24 top-0 hidden h-full w-96 -skew-x-12 bg-brand/20 lg:block" aria-hidden="true" />

        <motion.div style={{ opacity: fade }} className="relative mx-auto flex min-h-[92svh] max-w-7xl flex-col justify-center px-4 py-24 sm:px-6 lg:px-12">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="mb-5 flex items-center gap-3 font-mono text-xs font-bold uppercase tracking-[0.3em] text-sky-300"
            data-testid="hero-eyebrow"
          >
            <span className="h-px w-10 bg-sky-300/60" /> Family-Owned Since 1962 · Stanfield, NC
          </motion.p>

          <LineReveal
            lines={['Trusted HVAC & Plumbing', 'Service in Stanfield, NC']}
            className="max-w-4xl font-display text-4xl font-extrabold leading-[1.04] tracking-tight text-white sm:text-5xl lg:text-7xl"
          />

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.8 }}
            className="mt-6 max-w-2xl text-base leading-relaxed text-slate-200 sm:text-lg"
            data-testid="hero-subheadline"
          >
            For over 60 years, B.E. Holbrooks Co. has helped homeowners and businesses with dependable heating,
            air conditioning, plumbing, excavation, and comfort solutions.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.8 }}
            className="mt-9 flex flex-wrap gap-3"
          >
            <a
              href={SITE.phoneHref}
              data-testid="hero-call-button"
              className="flex items-center gap-2 rounded-full bg-brand px-7 py-4 font-display text-base font-bold text-white shadow-xl shadow-brand/40 transition-all hover:-translate-y-0.5 hover:bg-brand-dark"
            >
              <Phone className="h-5 w-5" /> Call {SITE.phone}
            </a>
            <Link
              to="/contact"
              data-testid="hero-schedule-button"
              className="flex items-center gap-2 rounded-full bg-white px-7 py-4 font-display text-base font-bold text-navy transition-all hover:-translate-y-0.5 hover:bg-sky-100"
            >
              <CalendarCheck className="h-5 w-5 text-brand" /> Schedule Service
            </Link>
            <Link
              to="/contact?form=install"
              data-testid="hero-estimate-button"
              className="flex items-center gap-2 rounded-full border border-white/40 px-7 py-4 font-display text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-white/10"
            >
              <FileText className="h-5 w-5" /> Request an Estimate
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.1, duration: 0.9 }}
            className="mt-14 grid max-w-4xl grid-cols-2 gap-3 lg:grid-cols-4"
            data-testid="hero-trust-badges"
          >
            {TRUST_BADGES.map((b) => (
              <div key={b.label} className="flex items-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 backdrop-blur-sm">
                <b.icon className="h-5 w-5 shrink-0 text-sky-300" />
                <span className="text-xs font-semibold leading-tight text-slate-200">{b.label}</span>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* 01 INTRO */}
      <section className="bg-white py-16 lg:py-24" data-testid="home-intro">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <Chapter num="01" label="Our Story" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Over 60 Years of Local Service
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600 sm:text-lg">
              B.E. Holbrooks Co. has provided plumbing, heating, and air conditioning services for over 60 years.
              From our shop and hardware store at 204 West Stanly Street in Stanfield, we have earned a reputation
              that keeps growing — by making customer satisfaction the top priority on every single job.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600 sm:text-lg">
              We serve residential and commercial customers across Stanly County and the greater Charlotte area,
              and we were hand-picked by American Standard as a Customer Care Dealer. When you call us, you get
              trained technicians, honest recommendations, and workmanship we stand behind.
            </p>
            <div className="mt-8 flex flex-wrap gap-6">
              {[['60+', 'Years in Business'], ['2', 'Generations of Craft'], ['10', 'NC Communities Served']].map(([num, label]) => (
                <div key={label}>
                  <p className="font-display text-3xl font-extrabold text-brand sm:text-4xl">{num}</p>
                  <p className="mt-1 font-mono text-xs uppercase tracking-widest text-slate-500">{label}</p>
                </div>
              ))}
            </div>
            <Link
              to="/about"
              data-testid="intro-about-link"
              className="mt-8 inline-flex items-center gap-1 font-display text-sm font-bold text-brand transition-colors hover:text-brand-dark"
            >
              Read our full story <ChevronRight className="h-4 w-4" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -left-4 -top-4 h-24 w-24 -skew-x-12 rounded-lg bg-brand/15" aria-hidden="true" />
              <img
                src={IMAGES.storefront}
                alt="B.E. Holbrooks Co. hardware and garden store at 204 West Stanly Street in Stanfield, NC"
                className="clip-frame relative w-full rounded-xl object-cover shadow-2xl shadow-navy/20"
                data-testid="intro-storefront-image"
              />
              <div className="absolute -bottom-5 left-6 flex items-center gap-3 rounded-xl bg-navy px-5 py-3 text-white shadow-xl">
                <Store className="h-5 w-5 text-sky-300" />
                <p className="text-sm font-semibold">Hardware & Garden Store<br /><span className="text-xs font-normal text-slate-300">204 West Stanly Street, Stanfield</span></p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 02 SERVICES */}
      <section className="bg-brand-soft/50 py-16 lg:py-24" data-testid="home-services">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Chapter num="02" label="What We Do" />
            <div className="mt-4 flex flex-wrap items-end justify-between gap-4">
              <h2 className="max-w-xl font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
                Complete Comfort, One Local Company
              </h2>
              <p className="max-w-md text-sm leading-relaxed text-slate-500 sm:text-base">
                HVAC, plumbing, excavation, and indoor air quality — every trade your property needs, from one accountable team.
              </p>
            </div>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICE_CARDS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.1}>
                <Link
                  to={s.path}
                  data-testid={s.testid}
                  className="group flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1.5 hover:shadow-xl"
                >
                  <div className="relative h-40 overflow-hidden">
                    <img
                      src={s.image}
                      alt={s.alt}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-navy/60 to-transparent" />
                    <div className="absolute bottom-3 left-3 rounded-lg bg-brand p-2 text-white shadow-lg">
                      <s.icon className="h-5 w-5" />
                    </div>
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <h3 className="font-display text-lg font-bold text-navy group-hover:text-brand">{s.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{s.text}</p>
                    <span className="mt-4 inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-widest text-brand">
                      Explore <ChevronRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 03 WHY CHOOSE */}
      <section className="relative overflow-hidden bg-navy py-16 grain lg:py-24" data-testid="home-why">
        <div className="absolute -right-32 top-0 h-full w-96 -skew-x-12 bg-brand/10" aria-hidden="true" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Chapter num="03" label="Why Holbrooks" light />
            <h2 className="mt-4 max-w-2xl font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Why Homeowners & Businesses Choose B.E. Holbrooks Co.
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {WHY_POINTS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur-sm transition-all hover:-translate-y-1 hover:border-sky-400/40" data-testid={`why-card-${i}`}>
                  <p className="font-mono text-3xl font-bold text-sky-400/60">0{i + 1}</p>
                  <h3 className="mt-3 font-display text-lg font-bold text-white">{p.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-300">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 04 SERVICE AREAS */}
      <section className="bg-white py-16 lg:py-24" data-testid="home-service-areas">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Chapter num="04" label="Where We Work" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Serving Stanfield, Locust, Charlotte & Surrounding Areas
            </h2>
            <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
              From our home base in Stanfield, our technicians serve communities across Stanly, Cabarrus,
              Mecklenburg, Union, and Gaston counties.
            </p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {CITIES.map((c, i) => (
              <Reveal key={c.slug} delay={i * 0.05}>
                <Link
                  to={cityPath(c.slug)}
                  data-testid={`city-card-${c.slug}`}
                  className="group flex items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-4 shadow-sm transition-all hover:-translate-y-1 hover:border-brand hover:shadow-md"
                >
                  <span className="flex items-center gap-2 font-display text-sm font-bold text-navy group-hover:text-brand">
                    <MapPin className="h-4 w-4 text-brand" /> {c.name}, NC
                  </span>
                  <ChevronRight className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-1 group-hover:text-brand" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 05 FINANCING & PROMOTIONS */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand to-navy py-16 grain lg:py-20" data-testid="home-financing-promo">
        <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <Chapter num="05" label="Financing & Savings" light />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-white sm:text-3xl lg:text-4xl">
              Comfort That Fits Your Budget
            </h2>
            <p className="mt-4 text-base leading-relaxed text-blue-100">
              A new comfort system should not have to wait. We offer financing options for qualified homeowners,
              seasonal promotions, and help claiming manufacturer rebates on high-efficiency American Standard equipment.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/financing" data-testid="home-financing-link" className="rounded-full bg-white px-6 py-3 font-display text-sm font-bold text-navy transition-all hover:-translate-y-0.5 hover:bg-sky-100">
                Explore Financing
              </Link>
              <Link to="/promotions" data-testid="home-promotions-link" className="rounded-full border border-white/40 px-6 py-3 font-display text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-white/10">
                View Promotions
              </Link>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <img
              src={IMAGES.americanStandard}
              alt="American Standard Customer Care Dealer badge - B.E. Holbrooks Co. Stanfield NC"
              className="clip-frame w-full rounded-xl bg-white object-contain p-4 shadow-2xl"
              data-testid="home-american-standard-image"
            />
          </Reveal>
        </div>
      </section>

      {/* FIELD GALLERY */}
      <section className="bg-white py-16 lg:py-24" data-testid="home-gallery">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <Chapter num="06" label="From the Field" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Our Team, On the Job
            </h2>
          </Reveal>
          <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {HOME_GALLERY.map((g, i) => (
              <Reveal key={g.alt} delay={i * 0.08}>
                <figure className="group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg">
                  <img
                    src={g.src}
                    alt={g.alt}
                    loading="lazy"
                    className="h-56 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    data-testid={`gallery-image-${i}`}
                  />
                  <figcaption className="p-3 font-mono text-xs font-bold uppercase tracking-widest text-slate-500">{g.caption}</figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-8 text-center">
            <Link to="/gallery" data-testid="home-gallery-link" className="inline-flex items-center gap-1 font-display text-sm font-bold text-brand transition-colors hover:text-brand-dark">
              View Full Project Gallery <ChevronRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <Reviews />
      <FAQSection faqs={HOME_FAQS} idPrefix="home-faq" />
      <CTABand testid="home-cta" />

      {/* CONTACT */}
      <section className="bg-white py-16 lg:py-24" data-testid="home-contact">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-2 lg:px-12">
          <Reveal>
            <Chapter num="06" label="Get In Touch" />
            <h2 className="mt-4 font-display text-2xl font-bold tracking-tight text-navy sm:text-3xl lg:text-4xl">
              Schedule HVAC or Plumbing Service Today
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Call us or send a request online — our office will follow up quickly to schedule your service,
              estimate, or comfort consultation.
            </p>
            <div className="mt-8 space-y-5">
              <a href={SITE.phoneHref} data-testid="home-contact-phone" className="flex items-center gap-4 rounded-xl border border-slate-200 p-4 transition-all hover:border-brand hover:shadow-md">
                <span className="rounded-full bg-brand p-3 text-white"><Phone className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-xs uppercase tracking-widest text-slate-400">Call or Text</span>
                  <span className="font-display text-xl font-extrabold text-navy">{SITE.phone}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-xl border border-slate-200 p-4" data-testid="home-contact-address">
                <span className="rounded-full bg-brand-soft p-3 text-brand"><MapPin className="h-5 w-5" /></span>
                <span>
                  <span className="block font-mono text-xs uppercase tracking-widest text-slate-400">Visit Us</span>
                  <span className="font-display text-base font-bold text-navy">{SITE.address}, {SITE.cityStateZip}</span>
                </span>
              </div>
              <div className="flex items-center gap-2 pl-1 text-sm text-slate-500">
                <Star className="h-4 w-4 fill-amberwarm text-amberwarm" />
                Serving Stanfield, Locust, Charlotte, Concord, Kannapolis, Albemarle, Norwood, Monroe, Gastonia & Huntersville
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <LeadForm />
          </Reveal>
        </div>
      </section>
    </>
  );
}
